import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useInView, useAnimation } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ProjectDetailPage = () => {
    const { projectId } = useParams();
    const containerRef = useRef(null);
    const heroRef = useRef(null);
    const imageRef = useRef(null);

    // Framer Motion animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.6,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const cardVariants = {
        hidden: { y: 30, opacity: 0, scale: 0.95 },
        visible: {
            y: 0,
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.7,
                ease: "easeOut"
            }
        }
    };

    // GSAP Animations
    useEffect(() => {
        const ctx = gsap.context(() => {
            // Hero section animation
            gsap.fromTo(heroRef.current,
                { y: 50, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    ease: "power3.out",
                    delay: 0.2
                }
            );

            // Image animation with 3D effect
            gsap.fromTo(imageRef.current,
                {
                    scale: 0.8,
                    opacity: 0,
                    rotationY: -15
                },
                {
                    scale: 1,
                    opacity: 1,
                    rotationY: 0,
                    duration: 1.2,
                    ease: "power3.out",
                    delay: 0.4
                }
            );

            // Floating animation for image
            gsap.to(imageRef.current, {
                y: -10,
                duration: 2,
                ease: "power2.inOut",
                yoyo: true,
                repeat: -1
            });

            // Tech stack cards animation
            gsap.fromTo(".tech-card",
                { y: 30, opacity: 0, scale: 0.9 },
                {
                    y: 0,
                    opacity: 1,
                    scale: 1,
                    duration: 0.6,
                    ease: "back.out(1.7)",
                    stagger: 0.1,
                    scrollTrigger: {
                        trigger: ".tech-section",
                        start: "top 80%",
                        end: "bottom 20%",
                        toggleActions: "play none none reverse"
                    }
                }
            );

            // Challenge items animation
            gsap.fromTo(".challenge-item",
                { x: -30, opacity: 0 },
                {
                    x: 0,
                    opacity: 1,
                    duration: 0.6,
                    ease: "power2.out",
                    stagger: 0.15,
                    scrollTrigger: {
                        trigger: ".challenges-section",
                        start: "top 80%",
                        toggleActions: "play none none reverse"
                    }
                }
            );

            // Improvement cards animation
            gsap.fromTo(".improvement-card",
                { y: 20, opacity: 0, scale: 0.95 },
                {
                    y: 0,
                    opacity: 1,
                    scale: 1,
                    duration: 0.5,
                    ease: "power2.out",
                    stagger: 0.1,
                    scrollTrigger: {
                        trigger: ".improvements-section",
                        start: "top 80%",
                        toggleActions: "play none none reverse"
                    }
                }
            );

        }, containerRef);

        return () => ctx.revert();
    }, [projectId]);

    // Animation controls for Framer Motion
    const controls = useAnimation();
    const isInView = useInView(containerRef, { once: true });

    useEffect(() => {
        if (isInView) {
            controls.start("visible");
        }
    }, [controls, isInView]);

    const projectsData = {
        'freelance-marketplace': {
            title: 'Freelance MarketPlace — Freelancing Website',
            subtitle: 'Project Team Self',
            description: 'A comprehensive freelance marketplace platform that connects clients with skilled professionals for short-term or project-based work. The platform enables flexible and remote collaboration on a global scale.',
            image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80',
            techStack: ['React.js', 'Node.js', 'Express.js', 'MongoDB', 'TailwindCSS', 'Firebase','netlify' ,'Vercel','JWT Authentication'],
            liveLink: 'https://meek-bubblegum-376b60.netlify.app/',
            githubLink: 'https://github.com/Azaharul-islam615/ph-assignment-10',
            challenges: [
                'Implementing real-time messaging between clients and freelancers',
                'Creating a secure payment gateway integration',
                'Building a robust user authentication and authorization system',
                'Designing an intuitive project management dashboard'
            ],
            improvements: [
                'Add video calling functionality for client-freelancer meetings',
                'Implement AI-powered project matching system',
                'Create mobile applications for iOS and Android',
                'Add multi-language support for global accessibility',
                'Integrate advanced analytics and reporting features'
            ]
        },
        'contesthub': {
            title: 'ContestHub - Related with Contest',
            subtitle: 'Contest Platform',
            description: 'An online platform where users can participate in various contests and challenges. It provides a competitive environment to showcase skills, earn recognition, and improve performance through gamification.',
            image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2126&q=80',
            techStack: ['JavaScript', 'React.js', 'Node.js', 'Express.js', 'MongoDB', 'Sripe', 'TailwindCss','Firebase','JWtAuhentication','Netlify','Vercel','TanstackQuery'],
            liveLink: 'https://fanciful-belekoy-e033d3.netlify.app/',
            githubLink: 'https://github.com/Azaharul-islam615/ph-assignment11-client',
            challenges: [
                'Creating real-time leaderboard updates during contests',
                'Implementing fair scoring and ranking algorithms',
                'Managing concurrent users during peak contest times',
                'Building an anti-cheating detection system'
            ],
            improvements: [
                'Add live streaming capabilities for contests',
                'Implement machine learning for personalized contest recommendations',
                'Create team-based contest features',
                'Add comprehensive analytics dashboard for organizers',
                'Integrate social media sharing and community features'
            ]
        },
        'toy-topia': {
            title: 'Toy-Topia',
            subtitle: 'E-commerce Web App',
            description: 'An online toy marketplace offering a wide range of fun and educational toys for kids. The platform provides a user-friendly shopping experience with secure ordering and fast delivery options.',
            image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80',
            techStack: ['React.js', 'Figma', 'CSS3', 'JavaScript', 'Firebase', 'TailwindCss', 'Material-UI'],
            liveLink: 'https://bejewelled-semolina-0de8b3.netlify.app/',
            githubLink: 'https://github.com/mdazaharul/toy-topia',
            challenges: [
                'Designing an intuitive interface suitable for parents and children',
                'Implementing secure payment processing for online transactions',
                'Creating an efficient inventory management system',
                'Building responsive design for various device sizes'
            ],
            improvements: [
                'Add AR/VR features for virtual toy testing',
                'Implement AI-powered age-appropriate toy recommendations',
                'Create a loyalty program and rewards system',
                'Add wishlist and gift registry features',
                'Integrate customer review and rating system with photos'
            ]
        }
    };

    const project = projectsData[projectId];

    if (!project) {
        return (
            <div className="min-h-screen pt-32 px-6 bg-background-light dark:bg-background-dark">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Project Not Found</h1>
                    <p className="text-gray-600 dark:text-gray-400 mb-8">The project you're looking for doesn't exist.</p>
                    <Link
                        to="/portfolio"
                        className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
                    >
                        <span className="material-icons-round text-sm">arrow_back</span>
                        Back to Portfolio
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            ref={containerRef}
            className="min-h-screen pt-24 sm:pt-28 md:pt-32 px-4 sm:px-6 lg:px-8 bg-background-light dark:bg-background-dark"
            variants={containerVariants}
            initial="hidden"
            animate={controls}
        >
            <div className="max-w-6xl mx-auto">
                {/* Back Button */}
                <motion.div variants={itemVariants}>
                    <Link
                        to="/portfolio"
                        className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium mb-6 sm:mb-8 transition-colors touch-manipulation"
                    >
                        <span className="material-icons-round text-sm">arrow_back</span>
                        Back to Portfolio
                    </Link>
                </motion.div>

                {/* Project Header */}
                <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 mb-12 sm:mb-16">
                    <motion.div ref={heroRef} variants={itemVariants}>
                        <motion.div
                            className="inline-flex items-center gap-2 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark px-3 py-1.5 rounded-full text-xs font-medium mb-3 sm:mb-4"
                            whileHover={{ scale: 1.05 }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            <motion.span
                                className="w-2 h-2 bg-primary rounded-full"
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            />
                            {project.subtitle}
                        </motion.div>

                        <motion.h1
                            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 leading-tight"
                            variants={itemVariants}
                        >
                            {project.title}
                        </motion.h1>

                        <motion.p
                            className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-6 sm:mb-8"
                            variants={itemVariants}
                        >
                            {project.description}
                        </motion.p>

                        {/* Action Buttons */}
                        <motion.div
                            className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4"
                            variants={itemVariants}
                        >
                            <motion.a
                                href={project.liveLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-4 sm:px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-all duration-300 text-sm sm:text-base touch-manipulation"
                                whileHover={{ y: -2, scale: 1.05 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <span className="material-icons-round text-sm">launch</span>
                                View Live Project
                            </motion.a>
                            <motion.a
                                href={project.githubLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 bg-gray-900 dark:bg-gray-700 text-white px-4 sm:px-6 py-3 rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-600 transition-all duration-300 text-sm sm:text-base touch-manipulation"
                                whileHover={{ y: -2, scale: 1.05 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" fillRule="evenodd"></path>
                                </svg>
                                GitHub Repository
                            </motion.a>
                        </motion.div>
                    </motion.div>

                    {/* Project Image */}
                    <motion.div
                        className="relative order-first lg:order-last"
                        variants={itemVariants}
                    >
                        <div className="relative group">
                            <motion.div
                                className="absolute -inset-1 bg-gradient-to-r from-primary to-purple-500 rounded-xl sm:rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"
                                animate={{
                                    background: [
                                        "linear-gradient(45deg, #10b981, #8b5cf6)",
                                        "linear-gradient(45deg, #8b5cf6, #10b981)",
                                        "linear-gradient(45deg, #10b981, #8b5cf6)"
                                    ]
                                }}
                                transition={{ duration: 4, repeat: Infinity }}
                            />
                            <motion.img
                                ref={imageRef}
                                src={project.image}
                                alt={project.title}
                                className="relative w-full h-60 sm:h-72 md:h-80 object-cover rounded-xl sm:rounded-2xl shadow-2xl"
                                whileHover={{ scale: 1.02, rotateY: window.innerWidth > 768 ? 5 : 0 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            />
                        </div>
                    </motion.div>
                </div>

                {/* Content Sections */}
                <motion.div
                    className="grid lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12"
                    variants={containerVariants}
                >
                    {/* Technology Stack */}
                    <motion.div
                        className="tech-section bg-surface-light dark:bg-surface-dark rounded-2xl p-8 border border-border-light dark:border-border-dark"
                        variants={cardVariants}
                        whileHover={{ y: -5, scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <motion.h2
                            className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3"
                            variants={itemVariants}
                        >
                            <motion.span
                                className="material-icons-round text-primary"
                                animate={{ rotate: [0, 360] }}
                                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                            >
                                code
                            </motion.span>
                            Technology Stack
                        </motion.h2>
                        <div className="flex flex-wrap gap-3">
                            {project.techStack.map((tech, index) => (
                                <motion.span
                                    key={index}
                                    className="tech-card px-4 py-2 bg-primary/10 text-primary border border-primary/20 rounded-lg font-medium text-sm"
                                    whileHover={{
                                        scale: 1.1,
                                        backgroundColor: "rgba(16, 185, 129, 0.2)",
                                        y: -2
                                    }}
                                    transition={{ type: "spring", stiffness: 400 }}
                                >
                                    {tech}
                                </motion.span>
                            ))}
                        </div>
                    </motion.div>

                    {/* Challenges */}
                    <motion.div
                        className="challenges-section bg-surface-light dark:bg-surface-dark rounded-2xl p-8 border border-border-light dark:border-border-dark"
                        variants={cardVariants}
                        whileHover={{ y: -5, scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <motion.h2
                            className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3"
                            variants={itemVariants}
                        >
                            <motion.span
                                className="material-icons-round text-orange-500"
                                animate={{ scale: [1, 1.1, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                psychology
                            </motion.span>
                            Challenges Faced
                        </motion.h2>
                        <ul className="space-y-4">
                            {project.challenges.map((challenge, index) => (
                                <motion.li
                                    key={index}
                                    className="challenge-item flex items-start gap-3"
                                    whileHover={{ x: 5 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <motion.span
                                        className="w-2 h-2 bg-orange-500 rounded-full mt-2 shrink-0"
                                        animate={{ scale: [1, 1.2, 1] }}
                                        transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.2 }}
                                    />
                                    <span className="text-gray-600 dark:text-gray-400 leading-relaxed">{challenge}</span>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>
                </motion.div>

                {/* Future Improvements */}
                <motion.div
                    className="improvements-section mt-12 bg-surface-light dark:bg-surface-dark rounded-2xl p-8 border border-border-light dark:border-border-dark"
                    variants={cardVariants}
                    whileHover={{ y: -5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                >
                    <motion.h2
                        className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3"
                        variants={itemVariants}
                    >
                        <motion.span
                            className="material-icons-round text-blue-500"
                            animate={{ rotate: [0, 15, -15, 0] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            lightbulb
                        </motion.span>
                        Future Improvements & Plans
                    </motion.h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {project.improvements.map((improvement, index) => (
                            <motion.div
                                key={index}
                                className="improvement-card flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
                                whileHover={{ scale: 1.02, y: -2 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            >
                                <motion.span
                                    className="material-icons-round text-blue-500 text-sm mt-0.5"
                                    animate={{ x: [0, 5, 0] }}
                                    transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
                                >
                                    arrow_forward
                                </motion.span>
                                <span className="text-gray-700 dark:text-gray-300 leading-relaxed">{improvement}</span>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Call to Action */}
                <motion.div
                    className="mt-16 text-center pb-16"
                    variants={itemVariants}
                >
                    <motion.h3
                        className="text-2xl font-bold text-gray-900 dark:text-white mb-4"
                        variants={itemVariants}
                    >
                        Interested in this project?
                    </motion.h3>
                    <motion.p
                        className="text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto"
                        variants={itemVariants}
                    >
                        Feel free to explore the live demo or check out the source code. I'm always open to feedback and collaboration opportunities.
                    </motion.p>
                    <motion.div
                        className="flex flex-wrap justify-center gap-4"
                        variants={itemVariants}
                    >
                        <motion.div
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-all duration-300"
                            >
                                <span className="material-icons-round text-sm">mail</span>
                                Get In Touch
                            </Link>
                        </motion.div>
                        <motion.div
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Link
                                to="/portfolio"
                                className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300"
                            >
                                <span className="material-icons-round text-sm">folder</span>
                                View More Projects
                            </Link>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default ProjectDetailPage;